import { toast } from 'sonner'
import { parseApiError, resolveApiErrorMessage, translateApiErrorCode } from '@/lib/apiError'

interface NotifyOptions {
  description?: string
  traceId?: string
  duration?: number
}

function buildDescription(options?: NotifyOptions): string | undefined {
  if (!options) return undefined
  const parts: string[] = []
  if (options.description) parts.push(options.description)
  if (options.traceId) parts.push(`Trace-ID: ${options.traceId}`)
  return parts.length > 0 ? parts.join('\n') : undefined
}

export const notify = {
  success(message: string, options?: NotifyOptions) {
    toast.success(message, {
      description: buildDescription(options),
      duration: options?.duration ?? 4000,
    })
  },

  // Errors stay up until the user dismisses them (X button/swipe) — the customer
  // should be able to photograph the Trace-Id & co. at leisure. Overridable via options.duration.
  error(message: string, options?: NotifyOptions) {
    toast.error(message, {
      description: buildDescription(options),
      duration: options?.duration ?? Infinity,
    })
  },

  info(message: string, options?: NotifyOptions) {
    toast.info(message, {
      description: buildDescription(options),
      duration: options?.duration ?? 4000,
    })
  },

  warning(message: string, options?: NotifyOptions) {
    toast.warning(message, {
      description: buildDescription(options),
      duration: options?.duration ?? 5000,
    })
  },

  /**
   * Error toast from a raw Axios error: parses the backend `ErrorResponse`,
   * translates the `errorCode` and appends the debug lines (Message / Error code /
   * Trace-ID — for correlation with backend logs during customer support). `fallback` =
   * page-specific context text („Fehler beim …"), used when the code has no
   * translation of its own. Call explicitly in each mutation's `onError`/`catch` —
   * there is deliberately no global handler (nothing written = no toast).
   */
  apiError(error: unknown, fallback?: string) {
    const parsed = parseApiError(error)
    const codeText = translateApiErrorCode(parsed.errorCode)
    const message = resolveApiErrorMessage(parsed, fallback)
    const parts: string[] = []
    // If the code translation supplies the title, the page context
    // („Fehler beim …") moves into the description instead of being lost.
    if (codeText && fallback) parts.push(fallback)
    if (parsed.backendMessage) parts.push(`Message: ${parsed.backendMessage}`)
    if (parsed.errorCode) parts.push(`Error code: ${parsed.errorCode}`)
    if (parsed.traceId) parts.push(`Trace-ID: ${parsed.traceId}`)
    toast.error(message, {
      description: parts.length > 0 ? parts.join('\n') : undefined,
      // Stays until manually dismissed — debug info should not time out.
      duration: Infinity,
    })
  },
}
